import { useEffect, useMemo, useState } from 'react';
import { Button, Space } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TurndownService from 'turndown';
import { marked } from 'marked';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './RichTextEditor.css';

export interface RichTextEditorProps {
  value?: string;
  onChange?: (markdown: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  showToolbar?: boolean;
  showPreviewToggle?: boolean;
}

export function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'テキストを入力...',
  disabled = false,
  minHeight = 175,
  showToolbar = true,
  showPreviewToggle = true,
}: RichTextEditorProps) {
  const [isPreview, setIsPreview] = useState(false);
  const [internalValue, setInternalValue] = useState(value);

  const turndown = useMemo(() => {
    const service = new TurndownService({
      headingStyle: 'atx',
      emDelimiter: '*',
    });

    // 打消し線（strikethrough）のルールを追加
    service.addRule('strikethrough', {
      filter: ['del', 's', 'strike'],
      replacement: (content) => `~~${content}~~`,
    });

    return service;
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: marked.parse(value || '') as string,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = turndown.turndown(html);
      setInternalValue(markdown);
      onChange?.(markdown);
    },
  });

  // 外部からのvalue変更を反映
  useEffect(() => {
    if (editor && value !== internalValue) {
      const currentHtml = editor.getHTML();
      const targetHtml = marked.parse(value || '') as string;
      if (currentHtml !== targetHtml) {
        editor.commands.setContent(targetHtml, false);
        setInternalValue(value);
      }
    }
  }, [editor, value, internalValue]);

  // disabled状態の反映
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const handleAddLink = () => {
    if (!editor) return;
    const url = window.prompt('リンクURLを入力してください');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const ToolbarButton = ({
    onClick,
    isActive,
    icon,
    title,
  }: {
    onClick: () => void;
    isActive?: boolean;
    icon: React.ReactNode;
    title: string;
  }) => (
    <Button
      type={isActive ? 'primary' : 'text'}
      size="small"
      icon={icon}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      disabled={disabled}
      title={title}
    />
  );

  return (
    <div className={`rich-text-editor ${disabled ? 'disabled' : ''}`}>
      {(showToolbar || (showPreviewToggle && isPreview)) && (
        <div className="rich-text-editor-toolbar">
          {!isPreview && showToolbar && (
            <Space size={2}>
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleBold().run()}
                isActive={editor?.isActive('bold')}
                icon={<BoldOutlined />}
                title="太字"
              />
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                isActive={editor?.isActive('italic')}
                icon={<ItalicOutlined />}
                title="斜体"
              />
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleStrike().run()}
                isActive={editor?.isActive('strike')}
                icon={<StrikethroughOutlined />}
                title="取り消し線"
              />
              <div className="toolbar-divider" />
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                isActive={editor?.isActive('bulletList')}
                icon={<UnorderedListOutlined />}
                title="箇条書き"
              />
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                isActive={editor?.isActive('orderedList')}
                icon={<OrderedListOutlined />}
                title="番号付きリスト"
              />
              <div className="toolbar-divider" />
              <ToolbarButton
                onClick={handleAddLink}
                isActive={editor?.isActive('link')}
                icon={<LinkOutlined />}
                title="リンク"
              />
            </Space>
          )}
          {isPreview && <div />}
          {showPreviewToggle && (
            <Button
              type="text"
              size="small"
              icon={isPreview ? <EditOutlined /> : <EyeOutlined />}
              onClick={() => setIsPreview(!isPreview)}
            >
              {isPreview ? '編集に戻る' : 'プレビュー'}
            </Button>
          )}
        </div>
      )}

      <div
        className="rich-text-editor-content"
        style={{ minHeight: `${minHeight}px` }}
      >
        {isPreview ? (
          <div className="rich-text-editor-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
              {internalValue || 'プレビューする内容がありません。'}
            </ReactMarkdown>
          </div>
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>
    </div>
  );
}
